import React, { useState, useEffect } from "react";
import MainLayout from "@/components/layouts/MainLayout";
import { Button } from "@/components/ui/button";
import { Plus, Search, FileText, Pencil, Trash2, Download, Receipt, ShoppingCart, RotateCcw, CheckCircle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/contexts/AuthContext";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { FacturaForm } from "./components/FacturaForm";
import { Factura, DocumentType } from "@/models/factura";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import FacturaPDFTemplate from "./components/FacturaPDFTemplate";
import { usePDFGenerator } from "@/hooks/usePDFGenerator";
import { useScreenInfo } from "@/hooks/useScreenInfo";
import { useCompanyConfig } from "@/hooks/useCompanyConfig";

const FacturacionPage = () => {
  const queryClient = useQueryClient();
  const { user, isAdmin } = useAuth();
  const [searchTerm, setSearchTerm] = useState("");
  const [activeSearchTerm, setActiveSearchTerm] = useState("");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [facturaToEdit, setFacturaToEdit] = useState<Factura | null>(null);
  const [facturaToDelete, setFacturaToDelete] = useState<Factura | null>(null);
  const [documentTypeFilter, setDocumentTypeFilter] = useState<string>("all");
  
  // Hook para generar PDFs
  const { isGenerating, generateAndDownloadPDF } = usePDFGenerator();
  
  // Hook para información de pantallas
  const { getScreenInfo } = useScreenInfo();

  // Hook para configuración de empresa
  const { data: companyConfig } = useCompanyConfig();

  // Consulta para obtener las facturas
  const { data: facturas = [], isLoading: isLoadingFacturas, error: facturasError } = useQuery({
    queryKey: ['facturas', activeSearchTerm, isAdmin, user?.id],
    queryFn: async () => {
      try {
        if (!user) {
          throw new Error('Debes iniciar sesión para ver las facturas');
        }

        let query = supabase
          .from('facturas')
          .select('*');

        // Si no es administrador, solo mostrar sus propias facturas
        if (!isAdmin) {
          query = query.eq('user_id', user.id);
        }

        // Aplicar filtro de búsqueda si existe término
        if (activeSearchTerm) {
          query = query.ilike('cliente', `%${activeSearchTerm}%`);
        }

        const { data, error } = await query.order('created_at', { ascending: false });

        if (error) throw error;
        
        return data || [];
      } catch (error) {
        console.error('Error en la consulta de facturas:', error);
        throw error;
      }
    },
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    staleTime: 0
  });

  // Función para manejar la búsqueda
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setActiveSearchTerm(searchTerm.trim());
  };

  // Función para limpiar la búsqueda
  const handleClear = () => {
    setSearchTerm("");
    setActiveSearchTerm("");
  };

  // Efecto para recargar facturas cuando cambie el usuario o rol
  useEffect(() => {
    if (user) {
      queryClient.invalidateQueries({ queryKey: ['facturas'] });
    }
  }, [user, isAdmin, queryClient]);

  // Función para verificar si el usuario puede editar/eliminar una factura
  const canEditDelete = (factura: Factura) => {
    if (!user) return false;
    return isAdmin || factura.user_id === user.id;
  };

  // Función simple para eliminar factura
  const eliminarFactura = async (id: string) => {
    try {
      // Primero obtener la factura para tener el ad_id
      const { data: factura, error: facturaError } = await supabase
        .from('facturas')
        .select('*, ad_id')
        .eq('id', id)
        .single();

      if (facturaError) throw facturaError;

      // Guardar el ad_id si existe
      const adId = factura.ad_id;

      // Primero eliminar la factura
      const { error: deleteError } = await supabase
        .from('facturas')
        .delete()
        .eq('id', id);

      if (deleteError) throw deleteError;

      // Si la factura tenía un logo, eliminarlo
      if (factura.logo_url) {
        const logoPath = factura.logo_url.split('/').pop();
        if (logoPath) {
          const { error: storageError } = await supabase.storage
            .from('facturas')
            .remove([`facturas-logos/${logoPath}`]);
          
          if (storageError) throw storageError;
        }
      }

      // Si la factura tenía un anuncio asociado
      if (adId) {
        console.log('Eliminando anuncio:', adId);
        
        // Obtener información del anuncio antes de eliminarlo
        const { data: adData, error: adFetchError } = await supabase
          .from('ads')
          .select('screen_id, screens_json')
          .eq('id', adId)
          .single();

        if (adFetchError) {
          console.error('Error al obtener información del anuncio:', adFetchError);
          throw adFetchError;
        }

        // Eliminar el anuncio
        const { error: adError } = await supabase
          .from('ads')
          .delete()
          .eq('id', adId);

        if (adError) {
          console.error('Error al eliminar anuncio:', adError);
          throw adError;
        }

        // Actualizar las pantallas afectadas
        if (adData && adData.screens_json) {
          const screenIds = adData.screens_json.map((screen: any) => screen.screenId);
          console.log('Actualizando pantallas:', screenIds);
          
          // Obtener las pantallas actuales
          const { data: screens, error: screensError } = await supabase
            .from('screens')
            .select('id, occupied_spaces')
            .in('id', screenIds);

          if (screensError) throw screensError;

          // Actualizar cada pantalla
          for (const screen of screens) {
            const { error: updateError } = await supabase
              .from('screens')
              .update({
                occupied_spaces: Math.max(0, screen.occupied_spaces - 1)
              })
              .eq('id', screen.id);

            if (updateError) throw updateError;
          }
        }
      }

      toast.success('Factura y anuncio eliminados exitosamente');
      setFacturaToDelete(null);
      queryClient.invalidateQueries({ queryKey: ['facturas'] });
      queryClient.invalidateQueries({ queryKey: ['screens'] });
      queryClient.invalidateQueries({ queryKey: ['ads'] });
    } catch (err: any) {
      console.error('Error al eliminar:', err);
      toast.error('No se pudo eliminar la factura: ' + err.message);
      setFacturaToDelete(null);
    }
  };

  // Función para generar PDF de una factura
  const handleGeneratePDF = async (factura: Factura) => {
    try {
      // Obtener la información de todas las pantallas
      const { data: screensData, error: screensError } = await supabase
        .from('screens')
        .select('id, name, location')
        .in('id', factura.lineas.map(linea => linea.pantalla));

      if (screensError) {
        console.error('Error al obtener información de pantallas:', screensError);
        toast.error('Error al obtener información de pantallas');
        return;
      }

      if (!screensData || screensData.length === 0) {
        console.error('No se encontró información de las pantallas');
        toast.error('No se encontró información de las pantallas');
        return;
      }

      // Obtener la información del cliente desde la tabla clients
      let clienteInfo = null;
      if (factura.client_id) {
        const { data: clientData, error: clientError } = await supabase
          .from('clients')
          .select('name, phone, email, address, rnc')
          .eq('id', factura.client_id)
          .single();

        if (clientError) {
          console.error('Error al obtener información del cliente:', clientError);
          toast.error('Error al obtener información del cliente');
          return;
        }

        clienteInfo = clientData;
      }

      // Calcular valores localmente para el PDF
      // Si es cortesía, todos los valores son cero
      let subtotalBruto, descuentosTotal, subtotal, impuestos;
      
      if (factura.es_cortesia) {
        subtotalBruto = 0;
        descuentosTotal = 0;
        subtotal = 0;
        impuestos = 0;
      } else {
        // Filtrar solo líneas NO bonificadas para los cálculos (igual que en el formulario)
        const lineasNoBonificadas = factura.lineas.filter(linea => !linea.bonificada);
        
        subtotalBruto = lineasNoBonificadas.reduce((acc, linea) => 
          acc + (linea.precio * linea.cantidad_dias), 0
        );
        
        descuentosTotal = lineasNoBonificadas.reduce((acc, linea) => 
          acc + ((linea.precio * linea.cantidad_dias) * (linea.descuento / 100)), 0
        );
        
        subtotal = subtotalBruto - descuentosTotal;
        impuestos = subtotal * 0.18; // 18% ITBIS
      }

      // Enriquecer la factura con información de pantallas, cliente y cálculos
      const facturaEnriquecida = {
        ...factura,
        id: factura.id,
        // Agregar información del cliente obtenida de la tabla clients
        telefono: clienteInfo?.phone || '',
        correo: clienteInfo?.email || '',
        direccion: clienteInfo?.address || '',
        subtotal,
        descuentos: descuentosTotal,
        impuestos,
        lineas: factura.lineas.map(linea => ({
          ...linea,
          total: linea.bonificada ? 0 : (linea.precio * linea.cantidad_dias) * (1 - (linea.descuento || 0) / 100)
        }))
      };

      const timestamp = new Date().getTime();
      const fileName = `Factura_${factura.cliente}_${factura.ncf}_${timestamp}`;
      await generateAndDownloadPDF(
        <FacturaPDFTemplate 
          factura={facturaEnriquecida as any} 
          screens={screensData}
          companyConfig={companyConfig}
        />, 
        fileName
      );
    } catch (error) {
      console.error('Error al generar PDF:', error);
      toast.error('Error al generar el PDF');
    }
  };

  return (
    <MainLayout>
      <div className="container mx-auto p-6">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Facturación de Anuncios</h1>
            <p className="text-muted-foreground">
              Gestiona la facturación de los anuncios publicitarios
            </p>
          </div>
          <Button 
            className="bg-yellow-400 hover:bg-yellow-500 text-black"
            onClick={() => setIsDialogOpen(true)}
          >
            <Plus className="mr-2 h-4 w-4" />
            Facturar Anuncio
          </Button>
        </div>

        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Buscar Facturas</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSearch} className="flex flex-wrap gap-4 items-end">
              <div className="flex-1 min-w-[200px] relative">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    type="text"
                    placeholder="Buscar por nombre del cliente..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-9"
                  />
                </div>
              </div>
              <div className="flex gap-2">
                <Button 
                  type="submit" 
                  className="bg-yellow-400 hover:bg-yellow-500 text-black"
                  disabled={isLoadingFacturas}
                >
                  <Search className="mr-2 h-4 w-4" />
                  {isLoadingFacturas ? 'Buscando...' : 'Buscar'}
                </Button>
                <Button 
                  type="button" 
                  variant="outline"
                  onClick={handleClear}
                  disabled={isLoadingFacturas}
                >
                  Limpiar
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Lista de facturas */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Facturas Registradas</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {isLoadingFacturas && (
              <div className="text-center py-8 text-gray-500">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-400 mx-auto mb-4"></div>
                Cargando facturas...
              </div>
            )}
            
            {!isLoadingFacturas && facturasError && (
              <div className="text-center py-8">
                <div className="text-red-500 mb-4">
                  Error al cargar las facturas: {facturasError.message}
                </div>
                <Button 
                  onClick={() => queryClient.invalidateQueries({ queryKey: ['facturas'] })}
                  variant="outline"
                  size="sm"
                >
                  Intentar de nuevo
                </Button>
              </div>
            )}

            {!isLoadingFacturas && !facturasError && facturas.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                {activeSearchTerm ? 
                  `No se encontraron facturas para "${activeSearchTerm}"` :
                  'No hay facturas registradas'
                }
              </div>
            )}

            {!isLoadingFacturas && !facturasError && facturas.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr className="border-b">
                      <th className="text-left py-3 px-4 font-medium text-gray-700">Cliente</th>
                      <th className="text-left py-3 px-4 font-medium text-gray-700">NCF</th>
                      <th className="text-left py-3 px-4 font-medium text-gray-700">RNC</th>
                      <th className="text-left py-3 px-4 font-medium text-gray-700">Fecha</th>
                      <th className="text-left py-3 px-4 font-medium text-gray-700">Período</th>
                      <th className="text-center py-3 px-4 font-medium text-gray-700">Pantallas</th>
                      <th className="text-right py-3 px-4 font-medium text-gray-700">Total</th>
                      <th className="text-left py-3 px-4 font-medium text-gray-700">Vendedor</th>
                      <th className="text-center py-3 px-4 font-medium text-gray-700">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {facturas.map((factura) => (
                      <tr 
                        key={factura.id} 
                        className={`border-b hover:bg-gray-50 transition-colors ${
                          factura.user_id === user?.id ? 'bg-blue-50' : ''
                        }`}
                      >
                        <td className="py-3 px-4">
                          <div className="font-medium text-gray-900">{factura.cliente}</div>
                        </td>
                        <td className="py-3 px-4">
                          <span className="text-sm text-gray-600">{factura.ncf}</span>
                        </td>
                        <td className="py-3 px-4">
                          <span className="text-sm text-gray-600">{factura.rnc_cedula}</span>
                        </td>
                        <td className="py-3 px-4">
                          <span className="text-sm text-gray-600">
                            {new Date(factura.fecha).toLocaleDateString()}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <span className="text-sm text-gray-600">
                            {new Date(factura.fecha).toLocaleDateString()} - {new Date(factura.vencimiento).toLocaleDateString()}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-center">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {factura.lineas.length}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-right">
                          <span className="font-medium text-yellow-600">
                            RD$ {new Intl.NumberFormat('es-DO').format(factura.total)}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <span className="text-sm text-gray-600">
                            {factura.user_id === user?.id ? 'Tú' : user?.profile?.role === 'mantenimiento' ? 'Encargado de Mantenimiento' : 'Otro vendedor'}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex items-center justify-center gap-1">
                            {/* Botón PDF - disponible para todos */}
                            <Button
                              variant="outline"
                              size="sm" 
                              className="h-7 px-2 bg-green-50 hover:bg-green-100 text-green-700 border-green-200"
                              onClick={() => handleGeneratePDF(factura)}
                              disabled={isGenerating}
                              title="Descargar PDF"
                            >
                              <Download className="h-3 w-3" />
                            </Button>
                            
                            {canEditDelete(factura) && (
                              <>
                                <Button
                                  variant="outline"
                                  size="sm" 
                                  className="h-7 px-2"
                                  onClick={() => {
                                    setFacturaToEdit(factura);
                                    setIsDialogOpen(true);
                                  }}
                                  title="Editar factura"
                                >
                                  <Pencil className="h-3 w-3" />
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm" 
                                  className="h-7 px-2 text-red-600 hover:text-red-700 hover:bg-red-50"
                                  onClick={() => setFacturaToDelete(factura)}
                                  title="Eliminar factura"
                                >
                                  <Trash2 className="h-3 w-3" />
                                </Button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Dialog para crear/editar factura */}
        <Dialog 
          open={isDialogOpen} 
          onOpenChange={(open) => {
            setIsDialogOpen(open);
            if (!open) {
              setFacturaToEdit(null);
              queryClient.invalidateQueries({ queryKey: ['facturas'] });
            }
          }}
        >
          <DialogContent className="sm:max-w-[80%] w-[90vw] max-h-[85vh] overflow-y-auto">
            <DialogHeader className="pb-4">
              <DialogTitle>{facturaToEdit ? 'Editar Factura' : 'Nueva Factura'}</DialogTitle>
            </DialogHeader>
            <FacturaForm 
              onClose={() => {
                setIsDialogOpen(false);
                setFacturaToEdit(null);
                queryClient.invalidateQueries({ queryKey: ['facturas'] });
              }}
              facturaToEdit={facturaToEdit}
            />
          </DialogContent>
        </Dialog>

        {/* Dialog de confirmación para eliminar */}
        <AlertDialog 
          open={!!facturaToDelete} 
          onOpenChange={(open) => !open && setFacturaToDelete(null)}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>¿Está seguro de eliminar esta factura?</AlertDialogTitle>
              <AlertDialogDescription>
                Esta acción no se puede deshacer. Se eliminará permanentemente la factura
                {facturaToDelete && ` de ${facturaToDelete.cliente}`}.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancelar</AlertDialogCancel>
              <AlertDialogAction
                className="bg-red-600 hover:bg-red-700 text-white"
                onClick={() => facturaToDelete && eliminarFactura(facturaToDelete.id)}
              >
                Eliminar
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </MainLayout>
  );
};

export default FacturacionPage;
